import {
  ActionIcon,
  Box,
  Button,
  Group,
  Input,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Text
} from '@mantine/core'
import { TimeInput } from '@mantine/dates'
import { UseFormReturnType } from '@mantine/form'
import { IconPlus, IconTrash, IconWorld } from '@tabler/icons-react'
import { ChangeEvent, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { HorizontalField } from '@/components/ui-shared/HorizontalField/HorizontalField'
import { PatrolClipSeconds } from '@/types/vlm'
import { getBrowserTimezone } from '@/utils/date'
import { generateUUID } from '@/utils/uuid'
import { PipelineFormValues } from '../VlmPipelineForm'
import {
  PATROL_INTERVALS,
  PATROL_MAX_ROUND_TIMEOUT_MINUTES,
  PATROL_MAX_TIMES,
  PATROL_MIN_ROUND_TIMEOUT_MINUTES,
  defaultRoundTimeout,
  expandPatrolInterval,
  formatPatrolInterval,
  getTimezoneOptions,
  isValidPatrolTime
} from './patrolSchedule'
import { getScheduleGapSeconds } from './patrolValidation'

const CLIP_SECONDS_OPTIONS: PatrolClipSeconds[] = [10, 20, 30, 60]
const NEW_TIME = '12:00'
// Round times listed in the interval preview before it is cut short.
const PREVIEW_MAX_TIMES = 6

type PatrolScheduleStepProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const PatrolScheduleStep = ({ form }: PatrolScheduleStepProps) => {
  const intl = useIntl()
  const {
    patrolScheduleMode,
    patrolTimes,
    patrolTimezone,
    patrolInterval,
    patrolStartTime,
    patrolLegacyEvery,
    patrolClipSeconds,
    patrolRoundTimeoutMinutes,
    patrolRoundTimeoutTouched,
    patrolCameras
  } = form.values.source

  const cameraCount = patrolCameras.length
  const gapSeconds = getScheduleGapSeconds(form.values)
  const suggested = defaultRoundTimeout(cameraCount, gapSeconds)
  const timezoneOptions = getTimezoneOptions(
    patrolTimezone,
    getBrowserTimezone()
  )

  // Follow the suggestion until the user picks a timeout themselves.
  useEffect(() => {
    if (!patrolRoundTimeoutTouched && patrolRoundTimeoutMinutes !== suggested) {
      form.setFieldValue('source.patrolRoundTimeoutMinutes', suggested)
    }
  }, [form, patrolRoundTimeoutTouched, patrolRoundTimeoutMinutes, suggested])

  const leaveLegacyEvery = () =>
    form.setFieldValue('source.patrolLegacyEvery', false)

  const handleModeChange = (mode: string) => {
    form.setFieldValue(
      'source.patrolScheduleMode',
      mode === 'every' ? 'every' : 'at'
    )
    leaveLegacyEvery()
  }

  const handleIntervalChange = (value: string | null) => {
    const interval = PATROL_INTERVALS.find((option) => option === value)
    if (!interval) return
    form.setFieldValue('source.patrolInterval', interval)
    leaveLegacyEvery()
  }

  const handleStartTimeChange = (event: ChangeEvent<HTMLInputElement>) => {
    form.setFieldValue('source.patrolStartTime', event.currentTarget.value)
    leaveLegacyEvery()
  }

  const handleTimezoneChange = (value: string | null) => {
    form.setFieldValue('source.patrolTimezone', value ?? '')
    leaveLegacyEvery()
  }

  const handleClipSecondsChange = (value: string) => {
    const seconds = CLIP_SECONDS_OPTIONS.find(
      (option) => String(option) === value
    )
    if (seconds) form.setFieldValue('source.patrolClipSeconds', seconds)
  }

  const handleTimeoutChange = (value: number | string) => {
    form.setFieldValue('source.patrolRoundTimeoutTouched', true)
    form.setFieldValue(
      'source.patrolRoundTimeoutMinutes',
      typeof value === 'number' ? value : Number(value) || 0
    )
  }

  const getIntervalPreview = () => {
    if (patrolLegacyEvery)
      return intl.formatMessage(
        { id: 'vlm.pipelines.create.patrol.schedule.preview.every' },
        { interval: formatPatrolInterval(patrolInterval, intl) }
      )

    if (!isValidPatrolTime(patrolStartTime)) return null

    const times = expandPatrolInterval(patrolStartTime, patrolInterval)
    const listed = times.slice(0, PREVIEW_MAX_TIMES).join(', ')

    return intl.formatMessage(
      { id: 'vlm.pipelines.create.patrol.schedule.preview.interval' },
      {
        times: times.length > PREVIEW_MAX_TIMES ? `${listed}, …` : listed,
        timezone: patrolTimezone
      }
    )
  }

  const intervalPreview = getIntervalPreview()

  const timezoneSelect = (
    <Select
      label={intl.formatMessage({
        id: 'vlm.pipelines.create.patrol.schedule.timezone.label'
      })}
      leftSection={<IconWorld size={16} />}
      data={timezoneOptions}
      allowDeselect={false}
      searchable
      {...form.getInputProps('source.patrolTimezone')}
      onChange={handleTimezoneChange}
    />
  )

  return (
    <Paper p="lg" bg="gray.1">
      <Stack gap="xl">
        <SegmentedControl
          color="brand-primary"
          value={patrolScheduleMode}
          data={[
            {
              value: 'at',
              label: intl.formatMessage({
                id: 'vlm.pipelines.create.patrol.schedule.mode.at'
              })
            },
            {
              value: 'every',
              label: intl.formatMessage({
                id: 'vlm.pipelines.create.patrol.schedule.mode.every'
              })
            }
          ]}
          onChange={handleModeChange}
        />

        {patrolScheduleMode === 'at' ? (
          <Paper p="lg" withBorder>
            <Stack gap="lg">
              <Input.Wrapper
                label={
                  <FormattedMessage id="vlm.pipelines.create.patrol.schedule.times.label" />
                }
                description={
                  <FormattedMessage id="vlm.pipelines.create.patrol.schedule.times.description" />
                }
                error={form.errors['source.patrolTimes']}
              >
                <Stack gap="xs" mt="xs">
                  {patrolTimes.map((time, index) => (
                    <Group key={time.uuid} gap="xs">
                      <TimeInput
                        w={140}
                        aria-label={intl.formatMessage(
                          {
                            id: 'vlm.pipelines.create.patrol.schedule.times.item'
                          },
                          { index: index + 1 }
                        )}
                        value={time.value}
                        onChange={(event) =>
                          form.setFieldValue(
                            `source.patrolTimes.${index}.value`,
                            event.currentTarget.value
                          )
                        }
                      />
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={patrolTimes.length === 1}
                        aria-label={intl.formatMessage({
                          id: 'vlm.pipelines.create.patrol.schedule.times.remove'
                        })}
                        onClick={() =>
                          form.removeListItem('source.patrolTimes', index)
                        }
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}

                  <Group>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={14} />}
                      disabled={patrolTimes.length >= PATROL_MAX_TIMES}
                      onClick={() =>
                        form.insertListItem('source.patrolTimes', {
                          uuid: generateUUID(),
                          value: NEW_TIME
                        })
                      }
                    >
                      <FormattedMessage id="vlm.pipelines.create.patrol.schedule.times.add" />
                    </Button>
                  </Group>
                </Stack>
              </Input.Wrapper>

              {timezoneSelect}
            </Stack>
          </Paper>
        ) : (
          <Paper p="lg" withBorder>
            <Stack gap="lg">
              <Group gap="md" align="flex-start" grow>
                <Select
                  label={intl.formatMessage({
                    id: 'vlm.pipelines.create.patrol.schedule.interval.label'
                  })}
                  data={PATROL_INTERVALS.map((interval) => ({
                    value: interval,
                    label: formatPatrolInterval(interval, intl)
                  }))}
                  allowDeselect={false}
                  value={patrolInterval}
                  onChange={handleIntervalChange}
                />
                <TimeInput
                  label={intl.formatMessage({
                    id: 'vlm.pipelines.create.patrol.schedule.start.label'
                  })}
                  {...form.getInputProps('source.patrolStartTime')}
                  onChange={handleStartTimeChange}
                />
              </Group>

              {timezoneSelect}

              {intervalPreview && (
                <Text size="sm" c="dimmed">
                  {intervalPreview}
                </Text>
              )}
            </Stack>
          </Paper>
        )}

        <Paper p="lg" withBorder>
          <Stack gap="xl">
            <HorizontalField
              size="sm"
              label={
                <FormattedMessage id="vlm.pipelines.create.patrol.schedule.clip.label" />
              }
              description={
                <FormattedMessage id="vlm.pipelines.create.patrol.schedule.clip.description" />
              }
            >
              <SegmentedControl
                size="xs"
                color="brand-primary"
                miw={200}
                data={CLIP_SECONDS_OPTIONS.map((seconds) => ({
                  label: `${seconds}s`,
                  value: String(seconds)
                }))}
                value={String(patrolClipSeconds)}
                onChange={handleClipSecondsChange}
              />
            </HorizontalField>

            <NumberInput
              label={intl.formatMessage({
                id: 'vlm.pipelines.create.patrol.schedule.timeout.label'
              })}
              description={intl.formatMessage({
                id: 'vlm.pipelines.create.patrol.schedule.timeout.description'
              })}
              inputContainer={(input) => (
                <Group gap="xs" wrap="nowrap">
                  <Box w={120}>{input}</Box>
                  <Text size="sm">
                    <FormattedMessage id="vlm.pipelines.create.patrol.schedule.timeout.unit" />
                  </Text>
                </Group>
              )}
              min={PATROL_MIN_ROUND_TIMEOUT_MINUTES}
              max={PATROL_MAX_ROUND_TIMEOUT_MINUTES}
              allowDecimal={false}
              allowNegative={false}
              value={patrolRoundTimeoutMinutes}
              error={form.errors['source.patrolRoundTimeoutMinutes']}
              onChange={handleTimeoutChange}
            />
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  )
}
